import { useCallback, useMemo } from 'react'
import { useRouter } from 'next/router'
import { stringifyUrl } from '@ucheba/utils/helpers/router'
import { allTagName } from '@ucheba/store/specialities/search/constants'
import { IFacet } from '@ucheba/utils/types/search'
import { EDataKeys } from '@ucheba/utils/types/dataKeys'
import { spaceDigits } from '@ucheba/utils/helpers/number'

interface IUseSelectedFacets {
  (facets: { [key: string]: IFacet | IFacet[] }): {
    selectedFacets: TSelectedFacetsItem[]
    removeFacet: (key: string, id: string) => void
  }
}

type TSelectedFacetsItem = IFacet & {
  queryKey: string
  withValue?: boolean
}

export const useSelectedFacets: IUseSelectedFacets = (facets) => {
  const router = useRouter()
  const { query } = router

  const getTargetFacets = useCallback(
    (facetList: IFacet[], queryKey: string): TSelectedFacetsItem[] => {
      return facetList
        .reduce((acc, facetItem) => {
          if (facetItem.items && facetItem.items.length) {
            facetItem.items.forEach((item) => {
              let newItem = item

              if (item.name === allTagName) {
                newItem = { ...item }
                newItem.name = facetItem.name
              }

              acc.push(newItem)
            })
          } else {
            acc.push(facetItem)
          }

          return acc
        }, [] as IFacet[])
        .reduce((arr, targetFacetItem) => {
          const targetValue = String(targetFacetItem.value)
          const queryValue = Array.isArray(query[queryKey])
            ? query[queryKey]
            : [query[queryKey]]

          if (
            (Array.isArray(queryValue) && queryValue.includes(targetValue)) ||
            targetValue === queryValue
          ) {
            arr.push({
              ...targetFacetItem,
              value: String(targetFacetItem.value),
              queryKey,
            })
          }

          return arr
        }, [] as TSelectedFacetsItem[])
    },
    [query]
  )

  /** По квери параметрам из фасетов получаем полные данные о выбранных фасетах */
  const selectedFacets = useMemo(() => {
    if (!facets) return []

    const resultSelectedFacets: TSelectedFacetsItem[] = []

    Object.keys(query).forEach((queryKey) => {
      const facetName = queryKey.replace(/[[]]/gm, '')
      const facet = facets[facetName]

      if (!facet) return

      if (
        facetName === EDataKeys.priceTo ||
        facetName === EDataKeys.priceFrom ||
        facetName === EDataKeys.academicHoursFrom ||
        facetName === EDataKeys.academicHoursTo
      ) {
        const targetFacet = Array.isArray(facet) ? facet[0] : facet
        const str =
          facetName === EDataKeys.priceTo || facetName === EDataKeys.priceFrom
            ? 'р.'
            : 'ак. ч.'

        resultSelectedFacets.push({
          ...targetFacet,
          value: `${spaceDigits(Number(query[queryKey]))} ${str}`,
          withValue: true,
          queryKey,
        })
      } else if (!Array.isArray(facet)) {
        resultSelectedFacets.push({
          ...facet,
          value: String(facet.value),
          queryKey,
        })
      } else {
        const targetFacets = getTargetFacets(facet, queryKey)
        targetFacets.forEach((targetFacetsItem) =>
          resultSelectedFacets.push(targetFacetsItem)
        )
      }
    })

    return resultSelectedFacets
  }, [facets, query, getTargetFacets])

  const removeFacet = useCallback(
    (key: string, id: string) => {
      const newQueryName = {}
      Object.keys(query).forEach((el) => {
        const facetName = el.replace(/[[]]/gm, '')

        if (key === el) {
          const newQuery = query[key]
          if (Array.isArray(newQuery)) {
            newQueryName[facetName] = newQuery.filter((newId) => String(newId) !== id)
          }
        } else {
          newQueryName[facetName] = query[el]
        }
      })

      router.push(
        stringifyUrl(router.asPath, {
          set: {
            ...newQueryName,
          },
        }),
        undefined,
        {
          shallow: true,
        }
      )
    },
    [query, router]
  )

  return {
    selectedFacets,
    removeFacet,
  }
}
